import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';

import { Layout, Drawer, Button, Alert } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';

import { RootState } from '../../../../application/store';
import { ORDER_STATUS } from '../../../../utils/constants/order-status';
import { initWideFormOrderLine, initWideFormProduct, toggleWideFormDrawer } from '../../../../slices/WideForm/wideFormSlice';
import { toggleCheckoutDrawer } from '../../../../slices/System/systemSlice';
import { groupProductsByFacility } from '../../../../utils/data-format-enchanters/order-products-enchanter';
import { useOrder } from '../../../hooks/useOrder';
import { useQuotaAvailability } from '../../../hooks/useQuotaAvailability';
import {
    findQuotaViolations,
    quotaViolationsBannerLabel
} from '../../../../utils/quota-utils';

import './bv-basket-drawer.scss';

import BVEmpty from '../../BVEmpty/bv-empty';
import BVContainerLoader from '../../BVContainerLoader/bv-container-loader';
import BVBasketFooter from './components/BVBasketFooter/bv-basket-footer';
import BVSummaryFacilityGroup from './components/BVSummaryFacilityGroup/bv-summary-facility-group';
import WideFormDrawer from '../WideFormDrawer/wide-form-drawer';

const {
    Sider
} = Layout;

const BVBasketDrawer: React.FunctionComponent = () => {
    const {
        handleUpsertOrder,
        upsertOrderLoading,
        handleRetrieveOrder,
        retrieveOrderLoading
    } = useOrder();

    const bvOrderSummary = useSelector((state: RootState) => state.orderSummary);
    const bvWideForm = useSelector((state: RootState) => state.wideForm);
    const bvCustomer = useSelector((state: RootState) => state.customer);

    const { handleQuotaAvailability, availabilityBySapCode } = useQuotaAvailability();

    // The current order must be left out of the draft quantities, otherwise the open cart would count
    // itself among the other carts.
    useEffect(() => {
        handleQuotaAvailability(
            bvCustomer.id ? Number(bvCustomer.id) : undefined,
            bvOrderSummary.id
        );
    }, [bvCustomer.id, bvOrderSummary.id]);

    const quotaViolations = findQuotaViolations(bvOrderSummary.products, availabilityBySapCode);
    const quotaBanner = quotaViolationsBannerLabel(quotaViolations);

    const dispatch = useDispatch();

    const [orderSummaryDrawerVisible, setOrderSummaryDrawerVisible] = useState<boolean>(false);

    const groupByFacility = groupProductsByFacility(bvOrderSummary.products);

    const _handleCheckoutAction = () => {
        dispatch(toggleCheckoutDrawer(true));

        setOrderSummaryDrawerVisible(false);
    }

    const _handleUpsertAction = () => {
        handleUpsertOrder(bvOrderSummary, (orderId) => handleRetrieveOrder(orderId));
    }

    return (
        <Sider className="bv-basket-sidebar-container" width={65}>
            <Button
                type="primary"
                shape="circle"
                icon={<ShoppingCartOutlined />}
                size="large"
                style={{ position: 'fixed', bottom: '35px', right: '12.5px' }}
                onClick={() => setOrderSummaryDrawerVisible(true)}
            />

            <Drawer
                className="bv-basket-drawer-container"
                width={950}
                placement="right"
                closable={true}
                onClose={() => setOrderSummaryDrawerVisible(false)}
                visible={orderSummaryDrawerVisible}
                footer={<BVBasketFooter
                    disabled={upsertOrderLoading || retrieveOrderLoading}
                    quotaViolationsCount={quotaViolations.length}
                    onCheckout={_handleCheckoutAction}
                    onUpsert={_handleUpsertAction}
                />}
            >
                <div className="bv-basket-container">
                    {quotaBanner &&
                        <Alert
                            type="warning"
                            showIcon
                            message={quotaBanner}
                            style={{ marginBottom: '12px' }}
                        />
                    }

                    <BVEmpty dataSource={groupByFacility} disclaimer={'Your basket is currently empty.'}>
                        {_.map(groupByFacility, (dataItem, dataIndex) => (
                            <BVSummaryFacilityGroup
                                key={dataIndex}
                                disabled={bvOrderSummary.status.code !== ORDER_STATUS.DRAFT.code}
                                quotaViolations={quotaViolations}
                                {...dataItem}
                            />
                        ))}
                    </BVEmpty>

                    <WideFormDrawer
                        productId={bvWideForm.productId}
                        orderLineId={bvWideForm.orderLineId}
                        drawerVisible={bvWideForm.wideFormDrawerVisible}
                        onDispose={() => {
                            dispatch(initWideFormOrderLine(-1));
                            dispatch(initWideFormProduct(-1));
                            dispatch(toggleWideFormDrawer(false));
                        }}
                    />

                    <BVContainerLoader loading={upsertOrderLoading || retrieveOrderLoading} />
                </div>
            </Drawer>
        </Sider>
    );
}

export default BVBasketDrawer;